import { useTranslation } from 'react-i18next';
import { EditableSection } from './EditableSection.js';
import { FeaturesEditor } from './FeaturesEditor.js';
import { FormatHint } from './FormatHint.js';
import { SpecTable } from './SpecTable.js';
import type { SpecFeatureRow, SpecFeatures } from '../../../lib/api/spec-requirements.js';

export interface FeaturesSectionProps {
  /** 今保存されている機能一覧。無い案件では null */
  features: SpecFeatures | null;
  /** この節を開いているか */
  editing: boolean;
  /** 直前の保存で残った注意 */
  warnings: string[];
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 編集を開く */
  onEdit: () => void;
  /** 保存する */
  onSave: (rows: SpecFeatureRow[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
  /** レビュー操作。呼び出し側が組み立てるので、ここでは `ReviewActions` を知らない */
  actions: React.ReactNode;
}

/**
 * 機能一覧の節。読むときは表、編集するときはフォーム。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 機能一覧（領域・要件・ASIS・TOBE・備考）を読み、編集して保存する節
 * @graph-connects FeaturesEditor [calls] 機能一覧の編集
 */
export function FeaturesSection({
  features,
  editing,
  warnings,
  saving,
  error,
  onEdit,
  onSave,
  onCancel,
  actions,
}: FeaturesSectionProps) {
  const { t } = useTranslation();

  return (
    <EditableSection
      version={features?.version}
      heading={t('spec.artifact.features')}
      action={t(features === null ? 'spec.action.write' : 'spec.action.edit')}
      editing={editing}
      warnings={warnings}
      onEdit={onEdit}
    >
      {editing ? <FormatHint artifact="features" label={t('spec.artifact.features')} /> : null}
      {editing ? (
        <FeaturesEditor
          features={features}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      ) : features === null ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">{t('spec.features.empty')}</p>
      ) : (
        <>
          {/* 訳が無い・古いときは原文の行が返る。空で隠されていないので、
              読んでいるものが原文だと分かるように断る */}
          {features.untranslated === true ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.features.untranslated')}
            </p>
          ) : null}
          <SpecTable
            numbered={{ prefix: 'F', heading: t('spec.section.rowNumber') }}
            headings={[
              t('spec.features.columns.area'),
              t('spec.features.columns.requirement'),
              t('spec.features.columns.asis'),
              t('spec.features.columns.tobe'),
              t('spec.features.columns.note'),
            ]}
            rows={features.rows.map((row) => ({
              key: `${row.area}/${row.requirement}`,
              no: row.no,
              cells: [row.area, row.requirement, row.asis, row.tobe, row.note],
            }))}
          />
        </>
      )}
      {actions}
    </EditableSection>
  );
}
